import React, { useState, useEffect, useCallback } from 'react';
import { Menu, GripVertical, Save, Plus, Trash2, Eye, EyeOff, Edit2, Check, X, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Available icons for menu items - Genel + İslami İkonlar
const availableIcons = [
  // İslami İkonlar
  { name: 'Mosque', label: '🕌 Cami', category: 'İslami', emoji: '🕌' },
  { name: 'Crescent', label: '☪️ Hilal', category: 'İslami', emoji: '☪️' },
  { name: 'Quran', label: '📖 Kuran', category: 'İslami', emoji: '📖' },
  { name: 'Prayer', label: '🤲 Dua', category: 'İslami', emoji: '🤲' },
  { name: 'Kaaba', label: '🕋 Kabe', category: 'İslami', emoji: '🕋' },
  { name: 'Tasbih', label: '📿 Tesbih', category: 'İslami', emoji: '📿' },
  { name: 'Star', label: '⭐ Yıldız', category: 'İslami', emoji: '⭐' },
  { name: 'Lantern', label: '🏮 Fener', category: 'İslami', emoji: '🏮' },
  { name: 'Dates', label: '🌴 Hurma', category: 'İslami', emoji: '🌴' },
  { name: 'Moon', label: '🌙 Ay', category: 'İslami', emoji: '🌙' },
  // Genel İkonlar
  { name: 'BookOpen', label: '📖 Kitap', category: 'Genel', emoji: '📖' },
  { name: 'BookMarked', label: '📑 İşaretli Kitap', category: 'Genel', emoji: '📑' },
  { name: 'Users', label: '👥 Kullanıcılar', category: 'Genel', emoji: '👥' },
  { name: 'FileText', label: '📄 Dosya', category: 'Genel', emoji: '📄' },
  { name: 'Clock', label: '🕐 Saat', category: 'Genel', emoji: '🕐' },
  { name: 'List', label: '📋 Liste', category: 'Genel', emoji: '📋' },
  { name: 'Search', label: '🔍 Arama', category: 'Genel', emoji: '🔍' },
  { name: 'Home', label: '🏠 Ana Sayfa', category: 'Genel', emoji: '🏠' },
  { name: 'Music', label: '🎵 Müzik', category: 'Genel', emoji: '🎵' },
  { name: 'Settings', label: '⚙️ Ayarlar', category: 'Genel', emoji: '⚙️' },
  { name: 'Heart', label: '❤️ Kalp', category: 'Genel', emoji: '❤️' },
  { name: 'Bell', label: '🔔 Bildirim', category: 'Genel', emoji: '🔔' },
  { name: 'Globe', label: '🌍 Dünya', category: 'Genel', emoji: '🌍' },
  { name: 'Mic', label: '🎤 Mikrofon', category: 'Genel', emoji: '🎤' },
  { name: 'Headphones', label: '🎧 Kulaklık', category: 'Genel', emoji: '🎧' },
];

// Get emoji for icon name
const getIconEmoji = (iconName) => {
  const icon = availableIcons.find(i => i.name === iconName);
  return icon ? icon.emoji : '📌';
};

// Default menu items
const defaultMenuItems = [
  { id: 'menu_1', name: 'Anasayfa', path: '/', icon: 'BookOpen', order: 0, visible: true },
  { id: 'menu_2', name: 'Sureler', path: '/sureler', icon: 'BookMarked', order: 1, visible: true },
  { id: 'menu_3', name: 'Hafızlar', path: '/hafizlar', icon: 'Users', order: 2, visible: true },
  { id: 'menu_4', name: 'Meal', path: '/meal', icon: 'FileText', order: 3, visible: true },
  { id: 'menu_5', name: 'Namaz Vakitleri', path: '/namaz-vakitleri', icon: 'Clock', order: 4, visible: true },
];

const MenuManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPath, setEditPath] = useState('');
  const [editIcon, setEditIcon] = useState('BookOpen');
  const [draggedItem, setDraggedItem] = useState(null);

  // Load menu items
  const loadMenuItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/menu-items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          const sortedData = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));
          setMenuItems(sortedData);
        } else {
          setMenuItems([...defaultMenuItems]);
        }
      } else {
        setMenuItems([...defaultMenuItems]);
      }
    } catch (error) {
      console.error('Error loading menu items:', error);
      setMenuItems([...defaultMenuItems]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMenuItems();
  }, [loadMenuItems]);

  // Save menu items
  const saveMenuItems = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      // Ensure all items have proper structure
      const itemsToSave = menuItems.map((item, index) => ({
        id: item.id || `menu_${Date.now()}_${index}`,
        name: item.name || 'Menü',
        path: item.path || '/',
        icon: item.icon || 'BookOpen',
        order: index,
        visible: item.visible !== false
      }));

      const response = await fetch(`${BACKEND_URL}/api/admin/menu-items`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemsToSave)
      });
      
      if (response.ok) {
        toast({
          title: "✅ Başarılı",
          description: "Menü ayarları kaydedildi",
        });
        // Reload to get fresh data
        await loadMenuItems();
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Error saving menu items:', error);
      toast({
        title: "❌ Hata",
        description: "Menü ayarları kaydedilemedi: " + error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Toggle visibility
  const toggleVisibility = (itemId) => {
    setMenuItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, visible: !item.visible } : item
    ));
  };

  // Start editing
  const startEditing = (item) => {
    setEditingId(item.id);
    setEditName(item.name || '');
    setEditPath(item.path || '');
    setEditIcon(item.icon || 'BookOpen');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditPath('');
    setEditIcon('BookOpen');
  };

  // Save editing
  const saveEditing = () => {
    if (!editName.trim()) {
      toast({
        title: "Uyarı",
        description: "Menü adı boş olamaz",
        variant: "destructive"
      });
      return;
    }

    setMenuItems(prev => prev.map(item => 
      item.id === editingId 
        ? { ...item, name: editName.trim(), path: editPath.trim() || '/', icon: editIcon }
        : item
    ));
    cancelEditing();
  };

  // Add new item
  const addNewItem = () => {
    const newItem = {
      id: `menu_new_${Date.now()}`,
      name: 'Yeni Menü',
      path: '/yeni',
      icon: 'Mosque',
      order: menuItems.length,
      visible: true
    };
    setMenuItems(prev => [...prev, newItem]);
    startEditing(newItem);
  };

  // Delete item
  const deleteItem = (itemId) => {
    if (window.confirm('Bu menü öğesini silmek istediğinize emin misiniz?')) {
      setMenuItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  // Move item up
  const moveUp = (index) => {
    if (index === 0) return;
    const newItems = [...menuItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setMenuItems(newItems.map((item, i) => ({ ...item, order: i })));
  };

  // Move item down
  const moveDown = (index) => {
    if (index === menuItems.length - 1) return;
    const newItems = [...menuItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setMenuItems(newItems.map((item, i) => ({ ...item, order: i })));
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newItems = [...menuItems];
    const draggedItemContent = newItems[draggedItem];
    newItems.splice(draggedItem, 1);
    newItems.splice(index, 0, draggedItemContent);
    
    setMenuItems(newItems.map((item, i) => ({ ...item, order: i })));
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#D5C4A1]/30 border-t-[#8B7355] rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#5C4033]">Menü Yönetimi</h2>
          <p className="text-[#8B7355]">Site menülerini düzenleyin, sıralayın ve yönetin</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={addNewItem}
            variant="outline"
            className="border-[#8B7355] text-[#8B7355] hover:bg-[#F9F6F1]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Menü
          </Button>
          <Button 
            onClick={saveMenuItems} 
            disabled={saving}
            className="bg-[#8B7355] hover:bg-[#6B5744]"
          >
            {saving ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"></div>
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Kaydet
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Menu Items List */}
      <Card className="border-[#E8DCC8]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#5C4033]">
            <Menu className="w-5 h-5 text-[#8B7355]" />
            Menü Öğeleri
          </CardTitle>
          <CardDescription>Sürükleyerek veya ok butonlarıyla sıralamayı değiştirin</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {menuItems.map((item, index) => (
              <div
                key={item.id}
                draggable={editingId !== item.id}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                  draggedItem === index 
                    ? 'bg-[#F5EFE7] border-[#8B7355] shadow-lg opacity-50' 
                    : 'bg-white border-[#E8DCC8] hover:border-[#D5C4A1]'
                } ${!item.visible ? 'opacity-60 bg-gray-50' : ''}`}
              >
                {/* Drag Handle & Order Buttons */}
                <div className="flex flex-col gap-1">
                  <button 
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="p-1 text-[#8B7355] hover:bg-[#F5EFE7] rounded disabled:opacity-30"
                  >
                    ▲
                  </button>
                  <div className="cursor-grab active:cursor-grabbing px-1">
                    <GripVertical className="w-4 h-4 text-[#8B7355]" />
                  </div>
                  <button 
                    onClick={() => moveDown(index)}
                    disabled={index === menuItems.length - 1}
                    className="p-1 text-[#8B7355] hover:bg-[#F5EFE7] rounded disabled:opacity-30"
                  >
                    ▼
                  </button>
                </div>
                
                {/* Order Number */}
                <div className="w-8 h-8 rounded-full bg-[#F5EFE7] flex items-center justify-center text-sm font-bold text-[#5C4033] flex-shrink-0">
                  {index + 1}
                </div>
                
                {/* Icon */}
                <div className="text-2xl w-10 text-center flex-shrink-0">
                  {getIconEmoji(item.icon)}
                </div>
                
                {/* Content */}
                {editingId === item.id ? (
                  <div className="flex-1 flex flex-wrap items-center gap-2">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Menü Adı"
                      className="w-36 border-[#E8DCC8]"
                      autoFocus
                    />
                    <Input
                      value={editPath}
                      onChange={(e) => setEditPath(e.target.value)}
                      placeholder="/sayfa-yolu"
                      className="w-36 border-[#E8DCC8]"
                    />
                    <select
                      value={editIcon}
                      onChange={(e) => setEditIcon(e.target.value)}
                      className="px-3 py-2 border border-[#E8DCC8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B7355] text-sm"
                    >
                      <optgroup label="☪️ İslami">
                        {availableIcons.filter(i => i.category === 'İslami').map(icon => (
                          <option key={icon.name} value={icon.name}>{icon.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="📁 Genel">
                        {availableIcons.filter(i => i.category === 'Genel').map(icon => (
                          <option key={icon.name} value={icon.name}>{icon.label}</option>
                        ))}
                      </optgroup>
                    </select>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={saveEditing} className="bg-green-600 hover:bg-green-700 h-9 w-9 p-0">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEditing} className="h-9 w-9 p-0">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#5C4033] truncate">{item.name}</div>
                    <div className="text-sm text-[#8B7355] truncate">{item.path}</div>
                  </div>
                )}
                
                {/* Actions */}
                {editingId !== item.id && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEditing(item)}
                      className="text-[#8B7355] hover:bg-[#F5EFE7] h-9 w-9 p-0"
                      title="Düzenle"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleVisibility(item.id)}
                      className={`h-9 w-9 p-0 ${item.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                      title={item.visible ? 'Gizle' : 'Göster'}
                    >
                      {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteItem(item.id)}
                      className="text-red-500 hover:bg-red-50 h-9 w-9 p-0"
                      title="Sil"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {menuItems.length === 0 && (
            <div className="text-center py-8 text-[#8B7355]">
              <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Henüz menü öğesi yok</p>
              <Button onClick={addNewItem} className="mt-4 bg-[#8B7355] hover:bg-[#6B5744]">
                <Plus className="w-4 h-4 mr-2" />
                İlk Menüyü Ekle
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="border-[#E8DCC8]">
        <CardHeader>
          <CardTitle className="text-[#5C4033]">📱 Önizleme</CardTitle>
          <CardDescription>Aktif menülerin görünümü</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-[#5C4033] rounded-lg p-4">
            <div className="flex items-center gap-3 flex-wrap justify-center">
              {menuItems.filter(item => item.visible).map((item) => (
                <div 
                  key={item.id}
                  className="flex items-center gap-2 px-4 py-2 bg-[#F5EFE7] rounded-lg text-[#5C4033] text-sm font-medium"
                >
                  <span>{getIconEmoji(item.icon)}</span>
                  <span>{item.name}</span>
                </div>
              ))}
              {menuItems.filter(item => item.visible).length === 0 && (
                <p className="text-[#D5C4A1] text-sm">Görünür menü yok</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">💡 İpuçları</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Sürükleyerek veya ▲▼ butonlarıyla sıralayın</li>
              <li>👁 ikonu ile menüyü gizleyin/gösterin</li>
              <li>✏️ ikonu ile düzenleyin</li>
              <li>Değişiklikleri <strong>Kaydet</strong> butonuyla uygulayın</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuManagement;
